'use client';

/**
 * LegDiscovery: camps on one drive. A pill on the leg's row ("Camps on this
 * leg · 21") opens a list of the camps along THAT drive only, numbered in the
 * order you pass them: the same numbers as the pins on the map. The night
 * being chosen is said once, in the header ("Choosing Night 2"); each row
 * ends in one quiet "+" that makes the camp that night, between the leg's
 * two stops. Only the selected row's "+" is filled; the halfway pick is
 * marked with a badge, never a second filled button.
 *
 * The pill sits in the leg row; the list opens as a sheet laid over the
 * itinerary column (desktop, so the live map beside it plots the numbered
 * pins) or from the bottom (phones). Its own scroller with a pinned header:
 * it never scrolls away under the column's bars, and rows never hide half
 * under its header. Back, the close button or Escape returns to the trip.
 * State lives with the caller.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { detourLabel, filterCamps, legPositionLabel, recommendLegCamp, sortLegCamps, type CampOnLine, type LatLng, type LegSort } from '../../utils/campsAlong';
import LegMiniMap from './LegMiniMap';
import { CampFacts, CampThumb, campHref } from './CampBits';
import { Chip, columnRect, sheetFrame, type SheetFrame } from './CampSwapSheet';
import { useSheetOpen } from './useSheetOpen';
import { IconArrowLeft, IconCheck, IconChevronDown, IconChevronRight, IconMoon, IconPlus, IconRestart, IconTent, IconX } from './icons';

export type LegCampsState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; items: CampOnLine[]; widthKm: number; road: boolean; lineKm?: number };

/** Legs shorter than this don't get a camps pill: nobody camps between two towns 12 minutes apart. */
export const LEG_DISCOVERY_MIN_MINUTES = 60;

/** One phrase for the corridor, everywhere it's described. */
export function corridorPhrase(widthKm: number, road: boolean): string {
  return road ? `within ${widthKm} km of the road` : `within ${widthKm} km of a straight line between the stops`;
}

/** "3h 33m" / "45 min". */
export function formatLegDrive(minutes?: number | null): string {
  if (typeof minutes !== 'number' || !Number.isFinite(minutes) || minutes <= 0) return '';
  const m = Math.round(minutes);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h}h ${String(r).padStart(2, '0')}m` : `${h}h`;
}

/** "253 km · 3h 33m · 21 camps": the leg header's one line of facts. */
export function legSummary(km?: number | null, minutes?: number | null, count?: number | null): string {
  const bits: string[] = [];
  if (typeof km === 'number' && km > 0) bits.push(`${Math.round(km)} km`);
  const d = formatLegDrive(minutes);
  if (d) bits.push(d);
  if (typeof count === 'number') bits.push(`${count} ${count === 1 ? 'camp' : 'camps'}`);
  return bits.join(' · ');
}

/**
 * The last line of a leg's "+" box, under its few ideas: how many camps sit
 * on this drive, and a way into all of them ("22 camps on this drive · See
 * all"). The full list opens as LegDiscoveryPanel.
 */
export function LegCampsLink({
  toName,
  state,
  onOpen,
  onRetry,
}: {
  toName: string;
  state: LegCampsState;
  onOpen: () => void;
  onRetry: () => void;
}) {
  if (state.status === 'ready' && state.items.length === 0) return null;
  const row = 'mt-2 flex min-h-[44px] w-full items-center gap-2.5 rounded-tp-sm border-0 border-t border-solid border-tp-line bg-transparent px-1 pt-2 text-left';
  if (state.status === 'error') {
    return (
      <div className={row} data-testid="leg-camps-link">
        <IconTent className="h-4 w-4 flex-none text-tp-muted" />
        <p className="m-0 min-w-0 flex-1 text-tp-sm text-tp-muted">Couldn’t look for camps on this drive.</p>
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex h-8 min-h-0 flex-none items-center gap-1 rounded-tp-sm border-0 bg-transparent px-2 text-tp-sm font-semibold text-tp-brand hover:bg-tp-brand-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45"
        >
          <IconRestart className="h-3.5 w-3.5" /> Try again
        </button>
      </div>
    );
  }
  const count = state.status === 'ready' ? state.items.length : null;
  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={count === null}
      aria-label={count === null ? `Looking for camps on the drive to ${toName}` : `See all ${count} camps on the drive to ${toName}`}
      className={`${row} group/legcamps transition-colors duration-150 enabled:hover:bg-tp-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45 disabled:cursor-default`}
      data-testid="leg-camps-link"
    >
      <IconTent className="h-4 w-4 flex-none text-tp-brand" />
      <span className="min-w-0 flex-1 text-tp-sm text-tp-ink">
        {count === null ? (
          <span className="text-tp-muted">Looking for camps on this drive…</span>
        ) : (
          <>
            <span className="font-semibold tabular-nums">{count}</span> {count === 1 ? 'camp' : 'camps'} on this drive
          </>
        )}
      </span>
      {count !== null && (
        <span className="flex flex-none items-center gap-0.5 text-tp-sm font-semibold text-tp-brand">
          See all <IconChevronRight className="h-3.5 w-3.5" />
        </span>
      )}
    </button>
  );
}

const PAGE = 8;

export function LegDiscoveryPanel({
  id,
  fromName,
  toName,
  state,
  onAdd,
  onClose,
  onRetry,
  hoveredId,
  onHover,
  onVisibleChange,
  addedLabel,
  line,
  legKm,
  legMinutes,
  lineKm: lineLengthKm,
  fromShort,
  toShort,
  selectedId,
  onSelect,
}: {
  id: string;
  fromName: string;
  toName: string;
  state: LegCampsState;
  onAdd: (c: CampOnLine) => void;
  onClose: () => void;
  onRetry: () => void;
  /** Row lit by a map pin hover (and vice versa). */
  hoveredId?: number | null;
  onHover?: (id: number | null) => void;
  /** The rows on screen, numbered: the map plots exactly these. */
  onVisibleChange?: (rows: { camp: CampOnLine; n: number }[]) => void;
  /** The leg's road: the phone sheet's mini map. */
  line?: LatLng[] | null;
  /** What an added camp becomes ("Night 2"), said on every row's action. */
  addedLabel?: string;
  /** The drive itself, for the header ("253 km · 3h 33m"). */
  legKm?: number | null;
  legMinutes?: number | null;
  /** Length of the line the camps were measured along: the halfway mark. */
  lineKm?: number | null;
  /** Town names for the header ("Lakes Entrance → Eden"). */
  fromShort?: string;
  toShort?: string;
  /** Picked on the map or by clicking a row (sticky, unlike hover). */
  selectedId?: number | null;
  onSelect?: (id: number | null) => void;
}) {
  const [shown, setShown] = useState(PAGE);
  const [sort, setSort] = useState<LegSort>('order');
  const [powered, setPowered] = useState(false);
  const [bookable, setBookable] = useState(false);
  const [addedId, setAddedId] = useState<number | null>(null);
  const all = state.status === 'ready' ? state.items : [];
  // Numbers follow driving order whatever the sort, so pin 3 is always the
  // third camp you pass.
  const numberOf = useMemo(() => new Map(all.map((c, i) => [c.id, i + 1])), [all]);
  const filtered = useMemo(() => sortLegCamps(filterCamps(all, { powered, bookable }), sort), [all, powered, bookable, sort]);
  // Halfway along the SAME line the camps were measured on, so the pick
  // can't move between screens that report the drive differently.
  const recommended = useMemo(() => {
    const km = lineLengthKm ?? legKm ?? (all.length ? Math.max(...all.map((c) => c.alongKm)) : 0);
    return recommendLegCamp(all, km);
  }, [all, lineLengthKm, legKm]);
  const visible = filtered.slice(0, shown);
  const headingId = `${id}-title`;
  const listRef = useRef<HTMLOListElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const [stuck, setStuck] = useState(false);
  const [headH, setHeadH] = useState(112);
  // A hairline shadow once the header is pinned and rows pass under it;
  // rows scrolled into view land just below it, never half hidden.
  useEffect(() => {
    const check = () => {
      const sec = sectionRef.current;
      const head = headerRef.current;
      if (!sec || !head) return;
      setStuck(sec.scrollTop > 0 || head.getBoundingClientRect().top - sec.getBoundingClientRect().top > 1);
      setHeadH(head.offsetHeight);
    };
    check();
    window.addEventListener('scroll', check, true);
    window.addEventListener('resize', check);
    return () => {
      window.removeEventListener('scroll', check, true);
      window.removeEventListener('resize', check);
    };
  }, []);
  const [frame, setFrame] = useState<SheetFrame>(() =>
    typeof window === 'undefined' ? { mode: 'bottom' } : sheetFrame(columnRect(), { width: window.innerWidth, height: window.innerHeight })
  );
  useEffect(() => {
    const update = () => setFrame(sheetFrame(columnRect(), { width: window.innerWidth, height: window.innerHeight }));
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  // Focus starts on the sheet; Escape closes; focus goes back on close.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    sectionRef.current?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      if (prev && document.contains(prev)) prev.focus?.({ preventScroll: true });
    };
  }, []);
  const addTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useSheetOpen();
  useEffect(() => () => {
    if (addTimer.current) clearTimeout(addTimer.current);
  }, []);

  const visibleKey = visible.map((c) => c.id).join(',');
  useEffect(() => {
    onVisibleChange?.(visible.map((c) => ({ camp: c, n: numberOf.get(c.id) ?? 0 })));
    // visibleKey stands in for `visible` (a new array every render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleKey, onVisibleChange]);
  useEffect(() => () => onVisibleChange?.([]), [onVisibleChange]);

  // A pin picked on the map brings its row into view, below the header.
  // Hover alone doesn't scroll: the list would chase the pointer.
  useEffect(() => {
    if (selectedId == null) return;
    const row = listRef.current?.querySelector<HTMLElement>(`[data-camp-id="${selectedId}"]`);
    row?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedId]);
  useEffect(() => () => onSelect?.(null), [onSelect]);

  // Say what the action did, on the row, then make the change (which closes
  // the list: the leg it belonged to is now two legs).
  const add = (c: CampOnLine) => {
    if (addedId != null) return;
    setAddedId(c.id);
    addTimer.current = setTimeout(() => onAdd(c), 900);
  };

  const count = state.status === 'ready' ? all.length : null;
  const facts = legSummary(legKm, legMinutes, count);
  const from = fromShort || fromName;
  const to = toShort || toName;

  const side = frame.mode === 'side';
  const sheetClass =
    frame.mode === 'side'
      ? 'h-full bg-white shadow-[-12px_0_32px_-16px_rgba(16,24,40,0.35)]'
      : frame.mode === 'center'
        ? 'relative max-h-[86vh] w-full max-w-[480px] rounded-2xl bg-white shadow-2xl'
        : 'relative max-h-[88vh] w-full rounded-t-2xl bg-white shadow-2xl';

  const panel = (
    <section
      ref={sectionRef}
      id={id}
      role="dialog"
      aria-modal={side ? undefined : true}
      aria-labelledby={headingId}
      tabIndex={-1}
      className={`${sheetClass} tp-touch-scroll snap-y snap-proximity overflow-y-auto overscroll-contain outline-none`}
      // Rows snap to sit just under the pinned header, never half under it.
      style={{
        animation: side ? 'tpSlideIn 220ms cubic-bezier(0.2, 0.8, 0.2, 1) both' : 'tpFadeUp 200ms cubic-bezier(0.2, 0.8, 0.2, 1) both',
        scrollPaddingTop: headH,
        outline: 'none',
      }}
      data-testid="leg-discovery-sheet"
      data-frame={frame.mode}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Stays put while the rows scroll. Solid white: a translucent bar let
          the rows scrolling under it show through as a grey smudge. */}
      <header
        ref={headerRef}
        className={`sticky top-0 z-[5] border-b bg-white pb-2.5 pt-3 transition-shadow duration-150 ${
          stuck ? 'border-tp-line-strong shadow-[0_6px_12px_-10px_rgba(16,24,40,0.45)]' : 'border-tp-line'
        } ${frame.mode === 'bottom' ? 'rounded-t-2xl !pt-2' : ''}`}
      >
        {/* The grab handle rides in the pinned header, so it never scrolls
            away with the rows. Tap it (or the backdrop) to close. */}
        {frame.mode === 'bottom' && (
          <button
            type="button"
            onClick={onClose}
            tabIndex={-1}
            aria-hidden="true"
            className="mx-auto mb-1.5 flex h-4 w-16 min-h-0 items-center justify-center"
          >
            <span className="h-1 w-10 rounded-full bg-tp-line-strong" />
          </button>
        )}
        <div className="flex items-start gap-2 px-4">
          <button
            type="button"
            onClick={onClose}
            className="-ml-1.5 inline-flex h-8 w-8 min-h-0 flex-none items-center justify-center rounded-tp text-tp-muted hover:bg-tp-surface hover:text-tp-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-tp-brand"
            aria-label="Back to the itinerary"
            title="Back to the itinerary"
          >
            <IconArrowLeft className="h-4 w-4" />
          </button>
          <div className="min-w-0 flex-1">
            {addedLabel && (
              <p className="flex items-center gap-1 text-tp-sm font-semibold leading-tight text-tp-brand">
                <IconMoon className="h-3.5 w-3.5" /> Pick a camp for {addedLabel}
              </p>
            )}
            {/* Towns, not park names; the arrow travels with the destination
                so it never dangles at a line end. */}
            <h4
              id={headingId}
              className="mt-0.5 line-clamp-2 text-[16px] font-semibold leading-snug tracking-tight text-tp-ink [text-wrap:balance]"
              title={`${fromName} to ${toName}`}
            >
              {from}{' '}
              <span className="whitespace-nowrap">
                <span className="font-normal text-tp-faint" aria-label="to">→</span> {to}
              </span>
            </h4>
            <p className="mt-0.5 text-tp-sm tabular-nums leading-tight text-tp-muted" aria-live="polite">
              {state.status === 'loading' ? (facts ? `${facts} · finding camps…` : 'Finding camps…') : facts}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close camps on this leg"
            className="-mr-1.5 inline-flex h-8 w-8 min-h-0 flex-none items-center justify-center rounded-tp text-tp-muted hover:bg-tp-surface hover:text-tp-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-tp-brand"
          >
            <IconX className="h-4 w-4" />
          </button>
        </div>
        {state.status === 'ready' && all.length > 1 && (
          // One line that scrolls sideways: no chip orphaned on a second row.
          <div className="tp-chip-row mt-2.5 flex gap-1.5 overflow-x-auto pl-4" role="toolbar" aria-label="Sort and filter">
            <span role="radiogroup" aria-label="Sort" className="flex flex-none gap-1.5">
              <Chip active={sort === 'order'} onClick={() => setSort('order')}>Driving order</Chip>
              <Chip active={sort === 'closest'} onClick={() => setSort('closest')}>Closest to road</Chip>
            </span>
            {(all.some((c) => c.powered) || all.some((c) => c.bookingUrl)) && (
              <span className="mx-0.5 w-px flex-none self-stretch bg-tp-line" aria-hidden="true" />
            )}
            {all.some((c) => c.powered) && (
              <Chip pressed active={powered} onClick={() => setPowered((v) => !v)}>Powered</Chip>
            )}
            {all.some((c) => c.bookingUrl) && (
              <Chip pressed active={bookable} onClick={() => setBookable((v) => !v)}>Book online</Chip>
            )}
            {/* Room after the last chip, past the fade, when the row scrolls. */}
            <span className="w-10 flex-none" aria-hidden="true" />
          </div>
        )}
        {state.status === 'ready' && all.length > 1 && (
          <p className="mt-2 px-4 text-tp-xs leading-snug text-tp-muted" aria-live="polite">
            {sort === 'order'
              ? `In the order you reach them, leaving ${from}.`
              : 'Shortest detour off your drive first.'}
          </p>
        )}
        {/* Phones: the list covers the map, so a strip of the leg's
            geography shows where these camps sit. */}
        {!side && line && line.length > 1 && state.status === 'ready' && (
          <LegMiniMap
            line={line}
            pins={visible.map((c) => ({ id: c.id, lat: c.lat, lng: c.lng, n: numberOf.get(c.id) ?? 0 }))}
            fromLabel={from}
            toLabel={to}
            litId={selectedId ?? hoveredId ?? null}
          />
        )}
      </header>

      {state.status === 'loading' && (
        <ul className="divide-y divide-tp-line" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <li key={i} className="flex items-center gap-3 px-4 py-3">
              <div className="h-14 w-14 animate-pulse rounded-tp bg-tp-surface" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 w-3/4 animate-pulse rounded bg-tp-surface" />
                <div className="h-2.5 w-1/2 animate-pulse rounded bg-tp-surface" />
              </div>
              <div className="h-9 w-9 animate-pulse rounded-full bg-tp-surface" />
            </li>
          ))}
        </ul>
      )}

      {state.status === 'error' && (
        <div className="flex items-center justify-between gap-2 px-4 py-3 text-tp-sm text-tp-muted" role="alert">
          <span>Couldn’t reach the campsite search.</span>
          <button type="button" onClick={onRetry} className="inline-flex h-8 min-h-0 items-center gap-1 rounded-md px-2 font-semibold text-tp-brand hover:bg-tp-brand-soft">
            <IconRestart className="h-3.5 w-3.5" /> Retry
          </button>
        </div>
      )}

      {state.status === 'ready' && all.length === 0 && (
        <p className="px-4 py-4 text-tp-sm leading-snug text-tp-muted">
          No camps listed {corridorPhrase(state.widthKm, state.road)}. Camps in {fromName} and {toName} are under each night’s Swap.
        </p>
      )}

      {state.status === 'ready' && all.length > 0 && filtered.length === 0 && (
        <p className="px-4 py-4 text-tp-sm text-tp-muted">
          None of these match.{' '}
          <button
            type="button"
            className="font-semibold text-tp-brand hover:underline"
            onClick={() => {
              setPowered(false);
              setBookable(false);
            }}
          >
            Clear filters
          </button>
        </p>
      )}

      {state.status === 'ready' && filtered.length > 0 && (
        <>
          <ol ref={listRef} className="divide-y divide-tp-line" aria-labelledby={headingId}>
            {visible.map((c) => {
              const n = numberOf.get(c.id) ?? 0;
              const selected = selectedId === c.id;
              const lit = hoveredId === c.id || selected;
              const added = addedId === c.id;
              const pick = recommended?.id === c.id;
              return (
                <li
                  key={c.id}
                  className={`group flex snap-start items-center gap-3 py-3 pl-4 pr-3 transition-colors duration-150 ${
                    added ? 'bg-tp-ok-soft/70' : selected ? 'bg-tp-brand-soft shadow-[inset_3px_0_0_#1558b0]' : lit ? 'bg-tp-surface' : ''
                  }`}
                  style={{ scrollMarginTop: 8 }}
                  data-testid="leg-camp-row"
                  data-lat={c.lat}
                  data-lng={c.lng}
                  data-camp-id={c.id}
                  data-recommended={pick || undefined}
                  aria-current={selected || undefined}
                  // A real mouse only: a tap also fires mouseenter and left
                  // the row lit after the finger lifted.
                  onPointerEnter={(e) => e.pointerType === 'mouse' && onHover?.(c.id)}
                  onPointerLeave={(e) => e.pointerType === 'mouse' && onHover?.(null)}
                  onFocus={(e) => e.target.matches(':focus-visible') && onHover?.(c.id)}
                  onBlur={() => onHover?.(null)}
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest('a,button')) return;
                    onSelect?.(selected ? null : c.id);
                  }}
                >
                  {/* Photo with the same number as the pin on the map. */}
                  <span className="self-start pt-0.5">
                    <CampThumb camp={c} size={56} n={n} lit={selected} />
                    <span className="sr-only">Camp {n}: </span>
                  </span>
                  <div className="min-w-0 flex-1">
                    {pick && (
                      <span className="mb-1 inline-flex h-5 items-center gap-1 rounded-full bg-tp-brand-soft px-1.5 text-tp-xs font-semibold text-tp-brand ring-1 ring-inset ring-tp-brand/25">
                        <IconMoon className="h-3 w-3" /> Halfway pick
                      </span>
                    )}
                    <a
                      href={campHref(c)}
                      target="_blank"
                      rel="noopener"
                      className="line-clamp-2 text-tp-base font-semibold leading-snug text-tp-ink no-underline visited:text-tp-ink hover:underline hover:decoration-tp-faint hover:underline-offset-2"
                    >
                      {c.name}
                    </a>
                    <CampFacts camp={c} lead={legPositionLabel(c.alongKm, from)} distance={detourLabel(c.offKm)} />
                  </div>
                  {added ? (
                    <span
                      className="inline-flex h-9 flex-none items-center gap-1 rounded-tp bg-tp-ok px-3 text-tp-sm font-semibold text-white"
                      role="status"
                      style={{ animation: 'tpFadeIn 140ms ease-out both' }}
                    >
                      <IconCheck className="h-3.5 w-3.5" /> {addedLabel ?? 'Added'}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => add(c)}
                      disabled={addedId != null}
                      aria-label={`Stay at ${c.name}${addedLabel ? ` for ${addedLabel}` : ''}, between ${fromName} and ${toName}`}
                      // The same verb and shape as the Swap sheet: "Stay
                      // here", filled on the top row (or the one picked).
                      className={`inline-flex h-9 min-h-0 flex-none items-center rounded-tp border border-solid px-3 text-tp-sm font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-tp-brand disabled:opacity-40 ${
                        (selectedId != null ? selected : c.id === visible[0]?.id)
                          ? 'border-tp-brand bg-tp-brand text-white hover:bg-tp-brand-ink'
                          : 'border-tp-line-strong bg-white text-tp-ink hover:border-tp-brand hover:text-tp-brand'
                      }`}
                      data-testid="leg-camp-add"
                    >
                      Stay here
                    </button>
                  )}
                </li>
              );
            })}
          </ol>
          {filtered.length > shown && (
            <button
              type="button"
              onClick={() => setShown((k) => k + PAGE * 2)}
              className="flex w-full items-center justify-center gap-1 border-t border-tp-line py-2.5 text-tp-sm font-semibold text-tp-brand hover:bg-tp-brand-soft/60"
            >
              Show {Math.min(PAGE * 2, filtered.length - shown)} more
              <IconChevronDown className="h-3.5 w-3.5" />
            </button>
          )}
          <p className="border-t border-tp-line bg-tp-surface/80 px-4 py-2 text-tp-xs leading-snug text-tp-muted">
            Camps {corridorPhrase(state.widthKm, state.road)}. Rates come from each listing; check before you go.
          </p>
        </>
      )}
    </section>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(
    side && frame.mode === 'side' ? (
      // Over the itinerary column only: the map beside it stays live.
      <div className="fixed z-[1150]" style={{ left: frame.left, top: frame.top, width: frame.width, height: frame.height }}>
        {panel}
      </div>
    ) : (
      <div className={`fixed inset-0 z-[1150] flex justify-center ${frame.mode === 'center' ? 'items-center' : 'items-end'}`} role="presentation">
        <button
          type="button"
          aria-label="Close"
          tabIndex={-1}
          onClick={onClose}
          className="absolute inset-0 h-full w-full cursor-default bg-tp-ink/40"
          style={{ animation: 'tpFadeIn 160ms ease-out both' }}
        />
        {panel}
      </div>
    ),
    document.body
  );
}
